'use client';

import { AlertSeverity } from 'builder/components/enums/AlertSeverityEnum';
import {
    RecaptchaDisclaimer,
    RecaptchaFallbackDecision,
    RecaptchaFormChallenge,
    RecaptchaFormProvider,
    RecaptchaV3Action,
    useFormWithRecaptcha,
} from 'google-recaptcha/components';
import React, { type JSX, useCallback, useState } from 'react';

import { Alert, Button } from '@videoblocks/storywind';

import { requestResetAction } from './requestResetAction';

export function ResetTokenForm(): JSX.Element {
    const [bannerMessage, setBannerMessage] = useState('');

    const onSubmit = useCallback(
        async (
            formData: FormData,
            recaptcha: { token: string | undefined; version: 'v2' | 'v3' },
        ): Promise<RecaptchaFallbackDecision> => {
            const email = formData.get('email') as string;
            const result = await requestResetAction(email, recaptcha.token ?? '', recaptcha.version);

            if (result?.requiresV2Challenge) {
                return RecaptchaFallbackDecision.NeedsV2Challenge;
            }

            setBannerMessage(
                `Password reset instructions have been sent to ${email}. If you receive nothing, make sure the email you used is actually registered with a Storyblocks account.`,
            );
            return RecaptchaFallbackDecision.Handled;
        },
        [],
    );

    const recaptcha = useFormWithRecaptcha({
        action: RecaptchaV3Action.REQUEST_PASSWORD_RESET,
        onSubmit,
    });

    return (
        <RecaptchaFormProvider value={recaptcha}>
            {bannerMessage && (
                <Alert severity={AlertSeverity.success} onClose={() => setBannerMessage('')}>
                    {bannerMessage}
                </Alert>
            )}
            <div className="max-w-2xl w-full filter drop-shadow-xl rounded-md p-10 bg-white flex flex-col items-center sm:p-0 sm:drop-shadow-none sm:max-w-none">
                <h1 className="text-lg font-semibold text-left mt-0 mr-0 mb-4">Reset password for Storyblocks</h1>
                <p className="text-left">
                    Please enter the email associated with your account and we will email you a link to reset your
                    password.
                </p>
                <form
                    aria-label="Request Password Reset Form"
                    className="w-[20rem] m-0"
                    onSubmit={recaptcha.handleSubmit}
                >
                    <div className="text-left text-base flex flex-col mb-4">
                        <label htmlFor="email" className="font-semibold text-sm">
                            Email
                        </label>
                        <input
                            id="email"
                            type="email"
                            name="email"
                            required
                            placeholder="e.g. me@email.com"
                            autoComplete="email"
                            className="w-full border border-solid border-old-silver rounded h-10 pl-4"
                        />
                    </div>
                    <RecaptchaFormChallenge className="mb-4" />
                    <div className="text-center mb-4">
                        <Button id="request-reset-btn" type="submit" className="w-full" disabled={!recaptcha.canSubmit}>
                            Send reset link
                        </Button>
                    </div>
                    <RecaptchaDisclaimer className="text-center" />
                    <p className="text-center flex flex-col font-semibold">
                        <a href="/login" className="text-blue-700 hover:underline">
                            Go back to login
                        </a>
                    </p>
                </form>
            </div>
        </RecaptchaFormProvider>
    );
}
